import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, siteInfo } from '@/mocks/films';

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于木瓜影视',
    description: '木瓜影视是一家专业的在线视频观看平台，致力于为用户提供免费、高清、流畅的影视观看体验。',
    url: 'https://www.muguays.com/about',
    about: {
      '@type': 'Organization',
      name: '木瓜影视',
      foundingDate: '2020',
      description: '专业的免费在线影视观看平台',
      url: 'https://www.muguays.com',
    },
    inLanguage: 'zh-CN',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '木瓜影视需要注册吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要注册。木瓜影视所有影视内容均无需注册即可免费在线观看，打开网页即点即播。',
        },
      },
      {
        '@type': 'Question',
        name: '为什么观看不卡顿？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '木瓜影视采用先进的视频CDN分发技术和高性能服务器集群，确保视频播放流畅不缓冲，给您带来极佳的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '需要下载App或播放器吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。木瓜影视完全基于网页播放，支持所有主流浏览器（Chrome、Safari、Edge等），无需下载任何软件或播放器。',
        },
      },
      {
        '@type': 'Question',
        name: '影视资源更新速度快吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '木瓜影视拥有专业的资源更新团队，最新电视剧和电影资源基本在播出后24小时内即可上线，部分热门资源同步更新。',
        },
      },
      {
        '@type': 'Question',
        name: '支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '木瓜影视全面适配电脑、平板和手机等多种设备，支持全平台无缝观影体验，随时随地享受高清影视。',
        },
      },
      {
        '@type': 'Question',
        name: '是否支持海外访问？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '木瓜影视部署了全球CDN加速节点，海外用户同样可以流畅访问和观看，画质自动适配网络环境。',
        },
      },
    ],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormState('success');
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotField) {
        formData.delete('phone_alt');
      }

      const messageValue = formData.get('message');
      if (typeof messageValue === 'string' && messageValue.length > 500) {
        setFormError('留言内容不能超过500个字符');
        setFormState('error');
        return;
      }

      const response = await fetch('https://readdy.ai/api/form/d9nfs08bdkpk99j7gj00', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (!response.ok || (parsed && parsed.code !== 'OK')) {
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormError('留言发送失败，请稍后重试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '留言发送失败，请稍后重试');
        }
        setFormState('error');
        return;
      }

      setFormState('success');
      form.reset();
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 木瓜影视 | 品牌介绍与联系方式"
        description="木瓜影视品牌介绍：专业的免费在线影视观看平台，超过500万用户信赖。了解木瓜影视的品牌故事、用户口碑与联系方式。"
        keywords="木瓜影视,关于我们,品牌介绍,在线观看平台,免费影视,联系我们"
        canonicalPath="/about"
        ogTitle="关于木瓜影视 - 免费在线影视观看平台"
        ogDescription="了解木瓜影视的品牌故事、核心优势与用户口碑。超过500万用户的在线观影选择。"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <section className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">关于木瓜影视</h1>
          <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
            {siteInfo.slogan}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          {[
            { value: siteInfo.established, label: '成立时间', icon: 'ri-calendar-check-line' },
            { value: siteInfo.userCount, label: '服务用户', icon: 'ri-user-heart-line' },
            { value: siteInfo.filmCount, label: '影视资源', icon: 'ri-movie-line' },
            { value: siteInfo.updateFrequency, label: '更新频率', icon: 'ri-refresh-line' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                <i className={`${stat.icon} text-lg text-accent-600`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-accent-500 mb-1">{stat.value}</div>
              <div className="text-foreground-500 text-xs">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="mb-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">品牌故事</h2>
          <div className="bg-background-100 rounded-lg p-6 md:p-8 max-w-3xl mx-auto">
            <p className="text-foreground-700 text-sm leading-relaxed mb-4">
              {siteInfo.description}
            </p>
            <p className="text-foreground-700 text-sm leading-relaxed mb-4">
              自2020年成立以来，木瓜影视始终秉持<strong>"让观影更简单"</strong>的理念，通过持续的技术创新和内容积累，已发展成为行业内备受欢迎的在线影视观看平台。我们收录了超过10万部影视资源，涵盖<strong>电视剧、电影、综艺、动漫</strong>等多个分类，满足不同用户的观影需求。
            </p>
            <p className="text-foreground-700 text-sm leading-relaxed">
              木瓜影视采用先进的视频<strong>CDN加速技术</strong>和智能码率自适应系统，无论用户使用何种设备和网络环境，都能享受到<strong>高清流畅</strong>的观影体验。我们坚持以用户为中心，不断优化产品体验，让在线观影变得前所未有的简单和愉快。
            </p>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">用户口碑</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-background-100 rounded-lg p-5">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className={`${i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'} text-sm`}></i>
                    </div>
                  ))}
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  "{t.comment}"
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-900 text-sm font-medium">{t.name}</span>
                  <span className="text-foreground-400 text-xs">{t.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">常见问题</h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {[
              { q: '木瓜影视需要注册吗？', a: '不需要注册。木瓜影视所有影视内容均无需注册即可免费在线观看，打开网页即点即播，无需任何繁琐操作。' },
              { q: '为什么观看不卡顿？', a: '木瓜影视采用先进的视频CDN分发技术和高性能服务器集群，确保视频播放流畅不缓冲，自动适配您的网络环境。' },
              { q: '需要下载App或播放器吗？', a: '不需要。木瓜影视完全基于网页播放，支持所有主流浏览器（Chrome、Safari、Edge等），无需下载任何软件或播放器。' },
              { q: '影视资源更新速度快吗？', a: '木瓜影视拥有专业的资源更新团队，最新电视剧和电影资源基本在播出后24小时内即可上线，部分热门资源同步更新。' },
              { q: '支持哪些设备观看？', a: '木瓜影视全面适配电脑、平板和手机等多种设备，支持全平台无缝观影体验，随时随地享受高清影视。' },
              { q: '是否支持海外访问？', a: '木瓜影视部署了全球CDN加速节点，海外用户同样可以流畅访问和观看，画质自动适配网络环境。' },
            ].map((faq, idx) => (
              <details key={idx} className="bg-background-100 rounded-lg group">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-foreground-800 text-sm font-medium pr-4">{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                  </div>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2 text-center">联系我们</h2>
          <p className="text-foreground-500 text-sm text-center mb-8">
            有任何问题或建议？请随时通过以下方式联系我们，或填写表单留言。
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-background-100 rounded-lg p-4 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                <i className="ri-mail-line text-xl text-primary-500"></i>
              </div>
              <p className="text-xs text-foreground-400 mb-0.5">邮箱</p>
              <p className="text-foreground-800 text-sm">{siteInfo.contactEmail}</p>
            </div>
            <div className="bg-background-100 rounded-lg p-4 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                <i className="ri-wechat-line text-xl text-primary-500"></i>
              </div>
              <p className="text-xs text-foreground-400 mb-0.5">微信</p>
              <p className="text-foreground-800 text-sm">{siteInfo.contactWechat}</p>
            </div>
            <div className="bg-background-100 rounded-lg p-4 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                <i className="ri-customer-service-2-line text-xl text-primary-500"></i>
              </div>
              <p className="text-xs text-foreground-400 mb-0.5">QQ</p>
              <p className="text-foreground-800 text-sm">{siteInfo.contactQQ}</p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-background-100 rounded-lg p-6"
          >
            <input
              type="text"
              name="phone_alt"
              className="anti-spam-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="name" className="block text-foreground-600 text-sm mb-1.5">
                  您的称呼 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  placeholder="请输入您的称呼"
                  className="w-full bg-background-200 border border-background-300 rounded-md px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-foreground-600 text-sm mb-1.5">
                  电子邮箱 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  placeholder="请输入您的邮箱"
                  className="w-full bg-background-200 border border-background-300 rounded-md px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="subject" className="block text-foreground-600 text-sm mb-1.5">
                主题
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                placeholder="请输入留言主题"
                className="w-full bg-background-200 border border-background-300 rounded-md px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="message" className="block text-foreground-600 text-sm mb-1.5">
                留言内容 <span className="text-accent-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={4}
                maxLength={500}
                placeholder="请输入您想说的话（最多500字）"
                className="w-full bg-background-200 border border-background-300 rounded-md px-4 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
              ></textarea>
              <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
            </div>

            <button
              type="submit"
              disabled={formState === 'submitting'}
              className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              {formState === 'submitting' ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-2-line animate-spin"></i>
                  </div>
                  发送中...
                </span>
              ) : (
                '发送留言'
              )}
            </button>

            {formState === 'success' && (
              <div className="mt-4 bg-primary-100/50 text-primary-700 text-sm rounded-md px-4 py-3 text-center">
                留言发送成功！我们会尽快与您联系，感谢您的反馈。
              </div>
            )}

            {formState === 'error' && (
              <div className="mt-4 bg-accent-100/50 text-accent-700 text-sm rounded-md px-4 py-3 text-center">
                {formError || '留言发送失败，请稍后重试'}
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
}